/**
 * WebSocket client: joins the room for the current star system, streams the
 * local ship state at CLIENT_SEND_HZ, and renders remote players from a
 * snapshot buffer INTERP_DELAY_MS in the past (standard snapshot
 * interpolation — smooth under jitter, extrapolates briefly on loss).
 *
 * Remote ships are built from each player's modular layout (protocol v2)
 * and rebuilt live when a "refit" broadcast arrives.
 */
import * as THREE from "three";
import { systemPoses, stationFor, stationPose, padWorld, vehicleDef, type StationDef } from "@hyperlight/shared";
import type { StarSystem } from "../world/starsystem";
import geckos, { type ClientChannel } from "@geckos.io/client";
import {
  CLIENT_SEND_HZ,
  DEFAULT_LAYOUT,
  DEFAULT_STYLE,
  INTERP_DELAY_MS,
  PROTOCOL_VERSION,
  TRANSPORT_UNRELIABLE,
  combatAction,
  parseServerMsg,
  type CharacterStyle,
  type CombatActionId,
  type DroneActMsg,
  type DroneDeathMsg,
  type DroneFireMsg,
  type DroneMeleeMsg,
  type DroneState,
  type PlayerState,
  type RemotePlayer,
  type ShipLayout,
  type SystemRevealMsg,
  type WildlifeMsg,
} from "@hyperlight/shared";
import { makeNameSprite, OverheadBubble } from "../ship/shipMesh";
import { buildShipFromLayout, disposeShip, type BuiltShip, type WeaponMount } from "../ship/parts";
import { ThrusterTrails } from "../fx/trails";
import type { Collider } from "../fx/bolts";
import { Avatar } from "../character/avatar";
import { driveAvatar } from "../character/avatarDriver";
import type { VehicleView } from "../vehicles/proceduralSpeeder";
import { createVehicleView } from "../vehicles/glbVehicle";
import { driverSeat, vehicleRiderPose } from "../vehicles/rider";

interface Snapshot {
  time: number;
  p: [number, number, number];
  q: [number, number, number, number];
  v: [number, number, number];
  k: "ship" | "foot" | "vehicle";
  veh?: string;
  vb?: number;
  sp?: [number, number, number];
  sq?: [number, number, number, number];
  w?: string;
  /** Stowed (holstered-on-back) weapon id. */
  sw?: string;
  /** Docked at the system station (pad index) — pose is derived, not interpolated. */
  dock?: number;
  /** v15 on-foot animation state (discrete — read from the latest snapshot, not lerped). */
  j?: number;
  fp?: number;
  fl?: number;
  dp?: number;
  /** Unpowered landing anticipation (0–1). */
  fa?: number;
  wr?: number;
  wt?: number;
  lc?:number;
  lt?:number;
  le?:[number,number,number];
  am?: boolean;
  air?: boolean;
  cr?: boolean;
  mf?: number;
  mr?: number;
  cl?: number;
  pt?: number;
  ld?: boolean;
  bl?: boolean;
  sub?: number;
  /** Planet-LOCAL anchoring (foot near a body): index + local pose. */
  pi?: number;
  /** Anchor weight 0..1 (carry fade band) — blends local vs world interp. */
  ak?: number;
  loc?: [number, number, number];
  lq?: [number, number, number, number];
  /** Same for the parked/autopiloting hull. */
  spi?: number;
  sloc?: [number, number, number];
  slq?: [number, number, number, number];
}

/** Server-authoritative drone events, surfaced to the game layer. */
export interface DroneEvents {
  /** `timeMs` = the server snapshot stamp (drives the buffered drone render timeline). */
  snapshot(list: DroneState[], timeMs?: number): void;
  fire(msg: DroneFireMsg): void;
  /** A melee enemy struck — play the lunge/combo on the attacker (all clients). */
  melee(msg: DroneMeleeMsg): void;
  /** A one-shot humanoid action (dodge-roll). */
  act(msg: DroneActMsg): void;
  death(msg: DroneDeathMsg): void;
  loot(items: { id: string; n: number }[]): void;
  /** Node depletion snapshot/delta from the server. */
  nodes(list: { id: number; left: number }[]): void;
  /** A mining claim of OURS was honored. */
  mined(msg: { id: number; item: string; n: number; left: number }): void;
}

export class RemoteShip {
  group = new THREE.Group();
  readonly sysPos = new THREE.Vector3();
  readonly quat = new THREE.Quaternion();
  readonly vel = new THREE.Vector3();
  speed = 0;
  /** How the latest snapshot says to render this player. */
  kind: "ship" | "foot" | "vehicle" = "ship";
  vehicleId: string | null = null;
  vehicleBoost = 0;
  /**
   * The owner's ship pose while they're on foot (system space). Not
   * actually parked — the autopilot orbits it — so it interpolates between
   * snapshots like the player pose does.
   */
  readonly parkedPos = new THREE.Vector3();
  readonly parkedQuat = new THREE.Quaternion();
  /** Estimated autopilot speed (m/s), differenced for the trail FX. */
  parkedSpeed = 0;
  /** Held weapon item id while on foot (drives the avatar attachment). */
  weaponId: string | null = null;
  /** Equipped-but-holstered weapon shown on the back (PlayerState.sw). */
  stowedId: string | null = null;
  // v15 on-foot animation state — drives the remote avatar's flight/aim/move clips.
  netFlightPower=0;
  netFlightLaunchRemaining=0;
  netDivePreparation=0;
  netFlightApproach=0;
  netWallRunState=0;
  netWallRunTime=0;
  netLedgeClimbState=0;
  netLedgeClimbTime=0;
  readonly netLedgeAnchor=new THREE.Vector3();
  netJet: "none" | "hover" | "glide" | "dive" = "none";
  netAiming = false;
  netAirborne = false;
  netCrouch = false;
  netMoveFwd = 0;
  netMoveRight = 0;
  netClimb = 0;
  netPitch = 0;
  netBlocking = false; // unarmed-combat block (backpedal in the fighting stance)
  netSubmerge = 0; // submersion 0..1 (~1 ⇒ swimming) — selects the remote swim pose
  /** Latest system-space position (snapshot `p`) — the cone origin for a relayed jab. */
  netPos: [number, number, number] = [0, 0, 0];
  /** Reusable collider bound; the game rewrites kind/pos/radius per frame. */
  readonly collider: Collider = {
    kind: "ship",
    id: -1,
    pos: new THREE.Vector3(),
    radius: 5,
  };
  private lastParked = new THREE.Vector3();
  private hasLastParked = false;
  trails: ThrusterTrails;
  /** Lazy: built the first time the player is seen on foot. */
  avatar: Avatar | null = null;
  private built: BuiltShip;
  private vehicleView: VehicleView | null = null;
  /** Which def the live view was built for (a view is opaque about its own id). */
  private vehicleViewId: string | null = null;
  private nameSprite: THREE.Sprite;
  /** Overhead LOCAL chat line (spec §2); .text readable by probes. */
  readonly bubble = new OverheadBubble();
  /** Mirrors the last placeName() so a bubble lands at the right scale. */
  private nameOnFoot = false;
  private snapshots: Snapshot[] = [];
  private qa = new THREE.Quaternion();
  private qb = new THREE.Quaternion();
  private fwd = new THREE.Vector3();
  private tmpAxis = new THREE.Vector3();
  private tmpV = new THREE.Vector3();
  private tmpSpinQ = new THREE.Quaternion();
  private tmpQ2 = new THREE.Quaternion();
  private wPos = new THREE.Vector3();
  private wQuat = new THREE.Quaternion();
  private aPos = new THREE.Vector3();
  private aQuat = new THREE.Quaternion();
  private outLoc = new THREE.Vector3();
  /** Smoothed-correction state (planet-local): any discontinuity in the
   *  sampled pose — anchor-space switches, packet-gap recovery, late joins
   *  — is captured here and BLED OFF exponentially instead of stepping the
   *  rendered player. */
  private corr = new THREE.Vector3();
  private corrPi = -1;
  private prevLoc = new THREE.Vector3();
  private hasPrevLoc = false;
  /** True ground-relative speed when anchored (drives the walk gait —
   *  streamed velocity includes the planet carry, which made idle remotes
   *  jog in place). Null when unanchored. */
  groundSpeed: number | null = null;
  /** Parked-hull speed came from the anchored pair (skip world differencing). */
  parkedAnchored = false;
  /** Docked pad index from the newest snapshot (null = not docked). */
  dockPad: number | null = null;

  /** Character appearance; applied to the avatar when it exists. */
  style: CharacterStyle = DEFAULT_STYLE;

  /** Guild tag rendered as `[TAG] Name` (empty = no guild). Probe-readable. */
  tag = "";

  constructor(
    readonly id: number,
    readonly name: string,
    ship: ShipLayout,
    style: CharacterStyle,
    private scene: THREE.Scene,
    tag?: string,
  ) {
    this.style = style ?? DEFAULT_STYLE;
    this.tag = tag ?? "";
    this.built = buildShipFromLayout(ship ?? DEFAULT_LAYOUT, this.hue());
    this.group.add(this.built.group);
    this.nameSprite = makeNameSprite(name, this.tag || undefined);
    this.group.add(this.nameSprite);
    this.trails = new ThrusterTrails(scene);
    this.trails.setTips(this.built.thrusterTips);
  }

  restyle(style: CharacterStyle): void {
    this.style = style;
    this.avatar?.setStyle(style);
  }

  /** Their guild changed: re-bake the label in place (same parent, same
   *  scale/position — placeName owns those and must not be disturbed). */
  retag(tag: string): void {
    if (tag === this.tag) return;
    this.tag = tag;
    const old = this.nameSprite;
    const next = makeNameSprite(this.name, tag || undefined);
    next.scale.copy(old.scale);
    next.position.copy(old.position);
    old.parent?.add(next);
    old.removeFromParent();
    old.material.map?.dispose();
    old.material.dispose();
    this.nameSprite = next;
  }

  /** Move the name tag between the ship hull and the character. */
  private placeName(onFoot: boolean): void {
    this.nameOnFoot = onFoot && this.avatar !== null;
    if (this.nameOnFoot && this.avatar) {
      this.avatar.group.add(this.nameSprite);
      this.nameSprite.scale.set(5, 1.25, 1);
      this.nameSprite.position.set(0, 2.3, 0);
      this.bubble.place(this.avatar.group, 6, 3.0);
    } else {
      this.group.add(this.nameSprite);
      this.nameSprite.scale.set(12, 3, 1);
      this.nameSprite.position.set(0, 3.4, 0);
      this.bubble.place(this.group, 14, 5.1);
    }
  }

  /** A relayed LOCAL chat line from this player — floats above their head. */
  showBubble(text: string): void {
    if (this.nameOnFoot && this.avatar) this.bubble.show(text, this.avatar.group, 6, 3.0);
    else this.bubble.show(text, this.group, 14, 5.1);
  }

  private hue(): number {
    return (this.id * 0.137 + 0.05) % 1;
  }

  get mounts(): WeaponMount[] {
    return this.built.weaponMounts;
  }

  refit(ship: ShipLayout): void {
    this.group.remove(this.built.group);
    disposeShip(this.built);
    this.built = buildShipFromLayout(ship, this.hue());
    this.group.add(this.built.group);
    this.trails.setTips(this.built.thrusterTips);
  }

  /**
   * Anchor a world pose to the nearest planet's LOCAL frame, evaluated at
   * the snapshot's own server timestamp. A foot player rides rotating
   * ground: interpolating their WORLD coordinates against a planet whose
   * pose this client evaluates at a different moment (interp delay + any
   * clock skew) buries or floats them by the terrain-height difference.
   * Local coordinates are time-invariant for someone standing still.
   */
  private anchor(
    time: number,
    p: [number, number, number],
    q: [number, number, number, number],
    sys: StarSystem,
  ): {
    pi: number;
    ak: number;
    loc: [number, number, number];
    lq: [number, number, number, number];
  } | null {
    const def = sys.def;
    const poses = systemPoses(def, time / 1000);
    let pi = -1;
    let bestD = Infinity;
    for (let i = 0; i < def.planets.length; i++) {
      const c = poses[i].center;
      const d =
        Math.hypot(p[0] - c[0], p[1] - c[1], p[2] - c[2]) - def.planets[i].radius;
      if (d < bestD) {
        bestD = d;
        pi = i;
      }
    }
    if (pi < 0 || bestD > def.planets[pi].radius * 1.6) return null;
    // Same fade the local carry uses (full inside 2R, gone at 2.6R): the
    // anchored and world interpolation spaces BLEND across this band, so
    // crossing it never steps the rendered position.
    const ak = Math.max(0, Math.min(1, (def.planets[pi].radius * 1.6 - bestD) / (def.planets[pi].radius * 0.6)));
    const pd = def.planets[pi];
    this.tmpAxis.set(pd.spin.axis[0], pd.spin.axis[1], pd.spin.axis[2]);
    this.tmpSpinQ.setFromAxisAngle(this.tmpAxis, poses[pi].spinAngle).invert();
    const c = poses[pi].center;
    this.tmpV.set(p[0] - c[0], p[1] - c[1], p[2] - c[2]).applyQuaternion(this.tmpSpinQ);
    this.tmpQ2.set(q[0], q[1], q[2], q[3]).premultiply(this.tmpSpinQ);
    return {
      pi,
      ak,
      loc: [this.tmpV.x, this.tmpV.y, this.tmpV.z],
      lq: [this.tmpQ2.x, this.tmpQ2.y, this.tmpQ2.z, this.tmpQ2.w],
    };
  }

  push(time: number, s: PlayerState, sys: StarSystem | null): void {
    // A state can repeat across broadcasts (send and broadcast clocks
    // beat) — re-pushing it under a fresh timestamp reads as standing
    // still then doubling. One snapshot per state.
    const last = this.snapshots[this.snapshots.length - 1];
    if (last && time <= last.time) return;
    const k = s.k ?? "ship";
    let a = null;
    let sa = null;
    if (sys) {
      if (s.pl !== undefined && s.lp && s.lr && sys.def.planets[s.pl]) {
        // v9: SENDER-computed local pose — exact in the sender's frame,
        // immune to every clock. Receive-side reconstruction (below) drifted
        // with any sender simTime lag (their world pos embeds their poses).
        const pd = sys.def.planets[s.pl];
        const bestD = Math.hypot(s.lp[0], s.lp[1], s.lp[2]) - pd.radius;
        const ak = Math.max(0, Math.min(1, (pd.radius * 1.6 - bestD) / (pd.radius * 0.6)));
        if (ak > 0) a = { pi: s.pl, ak, loc: s.lp, lq: s.lr };
        if (s.slp && s.slr) sa = { pi: s.pl, ak: 1, loc: s.slp, lq: s.slr };
      } else if (k === "foot") {
        a = this.anchor(time, s.p, s.q, sys);
        // The parked/autopiloting hull rides the same rotating frame.
        if (s.sp && s.sq) sa = this.anchor(time, s.sp, s.sq, sys);
      }
    }
    this.snapshots.push({
      time,
      p: s.p,
      q: s.q,
      v: s.v,
      k,
      veh: s.veh,
      vb: s.vb,
      sp: s.sp,
      sq: s.sq,
      w: s.w,
      sw: s.sw,
      dock: s.dock,
      j: s.j,
      fp: s.fp,
      fl: s.fl,
      dp: s.dp,
      fa: s.fa,
      wr: s.wr,
      wt: s.wt,
      lc:s.lc,lt:s.lt,le:s.le,
      am: s.am,
      air: s.air,
      cr: s.cr,
      mf: s.mf,
      mr: s.mr,
      cl: s.cl,
      pt: s.pt,
      ld: s.ld,
      bl: s.bl,
      sub: s.sub,
      pi: a?.pi,
      ak: a?.ak,
      loc: a?.loc,
      lq: a?.lq,
      spi: sa?.pi,
      sloc: sa?.loc,
      slq: sa?.lq,
    });
    if (this.snapshots.length > 30) this.snapshots.shift();
    if (this.snapshots.length === 1) {
      this.sysPos.set(...s.p);
      this.quat.set(...s.q);
    }
  }

  /** Local anchor -> world via the planet's RENDERED pose (group transform
   *  + origin) so remotes sit on the ground the viewer actually sees. */
  private anchoredWorld(
    sys: StarSystem,
    pi: number,
    loc: THREE.Vector3,
    lq: THREE.Quaternion,
    origin: THREE.Vector3,
    outP: THREE.Vector3,
    outQ: THREE.Quaternion,
  ): boolean {
    const planet = sys.planets[pi];
    if (!planet) return false;
    outP.copy(loc).applyQuaternion(planet.group.quaternion).add(planet.group.position).add(origin);
    outQ.copy(planet.group.quaternion).multiply(lq);
    return true;
  }

  /** Interpolate state at the given server timestamp. */
  sample(renderTime: number, sys: StarSystem | null, origin: THREE.Vector3, dt: number): void {
    const snaps = this.snapshots;
    if (snaps.length === 0) return;
    const latest = snaps[snaps.length - 1];
    this.vel.set(...latest.v);
    // Mode + parked pose come straight from the newest snapshot — they're
    // discrete (and the parked ship doesn't move), so no interpolation.
    const wasFoot = this.kind === "foot";
    this.kind = latest.k;
    this.vehicleId = latest.veh ?? null;
    this.vehicleBoost = latest.vb ?? 0;
    if (this.kind === "vehicle") {
      const def = vehicleDef(this.vehicleId ?? "");
      if (def && (!this.vehicleView || this.vehicleViewId !== def.id)) {
        this.vehicleView?.dispose();
        this.vehicleView = createVehicleView(def, this.hue());
        this.vehicleViewId = def.id;
        this.group.add(this.vehicleView.group);
      }
    }
    // A foot snapshot without a parked-ship pose means the player owns NO ship
    // (vehicle-tiers: no default ship) — without this gate the hull renders at
    // parkedPos's (0,0,0) default, a ghost ship at the system origin.
    this.built.group.visible = this.kind !== "vehicle"
      && !(this.kind === "foot" && latest.sp === undefined);
    if (this.vehicleView) this.vehicleView.group.visible = this.kind === "vehicle";
    this.weaponId = latest.w ?? null;
    this.stowedId = latest.sw ?? null;
    this.dockPad = latest.dock ?? null;
    // v15 animation state (discrete) — straight from the newest snapshot.
    this.netJet = latest.j === 1 ? "hover" : latest.j === 2 ? "glide" : latest.j === 3 ? "dive" : "none";
    this.netFlightPower = latest.fp ?? 0;
    // Advance the explicit burst clock between packets; joining mid-launch
    // samples its current phase instead of replaying the start or freezing it.
    this.netFlightLaunchRemaining=Math.max(0,(latest.fl??0)-Math.max(0,renderTime+INTERP_DELAY_MS-latest.time)/1000);
    this.netDivePreparation=latest.dp??0;
    this.netFlightApproach = latest.fa ?? 0;
    this.netWallRunState = latest.wr ?? 0;
    this.netWallRunTime = latest.wt ?? 0;
    this.netLedgeClimbState=latest.lc??0;
    this.netLedgeClimbTime=Math.min(1,(latest.lt??0)+Math.max(0,renderTime+INTERP_DELAY_MS-latest.time)/1000);
    this.netLedgeAnchor.fromArray(latest.le??[0,0,0]);
    this.netAiming = latest.am ?? false;
    this.netAirborne = latest.air ?? false;
    this.netCrouch = latest.cr ?? false;
    this.netMoveFwd = latest.mf ?? 0;
    this.netMoveRight = latest.mr ?? 0;
    this.netClimb = latest.cl ?? 0;
    this.netPitch = latest.pt ?? 0;
    this.netBlocking = latest.bl ?? false;
    this.netSubmerge = latest.sub ?? 0;
    this.netPos = latest.p;
    // Landing for remotes: derived locally from their own air→ground edge + streamed climb (the avatar's
    // touchdown-edge selectLand picks phase/tier), so no networked hard-land flag is needed any more.
    if (latest.k === "foot" && latest.sp && latest.sq) {
      this.parkedPos.set(...latest.sp);
      this.parkedQuat.set(...latest.sq);
      if (!this.avatar) {
        this.avatar = new Avatar(this.scene, { crystalWings: true });
        this.avatar.setCrystalWeapon("sniper"); // other players carry crystal weapons too
        this.avatar.setStyle(this.style);
      }
    }
    if (latest.k === "vehicle" && !this.avatar) {
      this.avatar = new Avatar(this.scene, { crystalWings: true });
      this.avatar.setCrystalWeapon("sniper");
      this.avatar.setStyle(this.style);
    }
    if (wasFoot !== (this.kind === "foot")) this.placeName(this.kind === "foot");
    if (this.kind === "vehicle") this.nameSprite.position.set(0, 2.6, 0);
    // SIGNED forward speed — trails hide on reversing ships, so the
    // component along the nose matters, not the magnitude.
    this.qa.set(...latest.q);
    this.speed = this.vel.dot(this.fwd.set(0, 0, -1).applyQuaternion(this.qa));
    this.groundSpeed = null;
    let i = snaps.length - 1;
    while (i > 0 && snaps[i - 1].time > renderTime) i--;
    const b = snaps[i];
    const past = i === 0 || b.time <= renderTime;
    const a = past ? latest : snaps[i - 1];
    const t = past ? 1 : (renderTime - a.time) / Math.max(1, b.time - a.time);

    // --- 1) WORLD-space candidate: interp, or capped velocity extrap ---
    if (past) {
      const edt = Math.min(0.3, Math.max(0, (renderTime - latest.time) / 1000));
      this.wPos.set(
        latest.p[0] + latest.v[0] * edt,
        latest.p[1] + latest.v[1] * edt,
        latest.p[2] + latest.v[2] * edt,
      );
      this.wQuat.set(...latest.q);
    } else {
      this.wPos.set(
        a.p[0] + (b.p[0] - a.p[0]) * t,
        a.p[1] + (b.p[1] - a.p[1]) * t,
        a.p[2] + (b.p[2] - a.p[2]) * t,
      );
      this.qa.set(...a.q);
      this.qb.set(...b.q);
      this.wQuat.slerpQuaternions(this.qa, this.qb, t);
    }

    // --- 2) ANCHORED candidate: interpolated (or extrapolated) in the
    // body's frame, re-derived from the pose the viewer is RENDERING —
    // clock skew and interp delay cancel exactly on the ground ---
    let aPi = -1;
    let ak = 0;
    if (sys) {
      if (past && latest.pi !== undefined && latest.loc && latest.lq) {
        aPi = latest.pi;
        ak = latest.ak ?? 1;
        this.outLoc.set(...latest.loc);
        this.tmpQ2.set(...latest.lq);
        // Ground-frame extrapolation: local velocity from the previous
        // anchored snapshot (a stander extrapolates to standing still —
        // world-velocity extrap would walk them off the turning ground).
        const prev = snaps.length > 1 ? snaps[snaps.length - 2] : null;
        if (prev && prev.pi === aPi && prev.loc) {
          const sdt = Math.max(1, latest.time - prev.time) / 1000;
          const edt = Math.min(0.3, Math.max(0, (renderTime - latest.time) / 1000));
          this.outLoc.x += ((latest.loc[0] - prev.loc[0]) / sdt) * edt;
          this.outLoc.y += ((latest.loc[1] - prev.loc[1]) / sdt) * edt;
          this.outLoc.z += ((latest.loc[2] - prev.loc[2]) / sdt) * edt;
          this.groundSpeed =
            Math.hypot(latest.loc[0] - prev.loc[0], latest.loc[1] - prev.loc[1], latest.loc[2] - prev.loc[2]) / sdt;
        } else {
          this.groundSpeed = 0;
        }
      } else if (!past && a.pi !== undefined && a.pi === b.pi && a.loc && b.loc && a.lq && b.lq) {
        aPi = a.pi;
        ak = (a.ak ?? 1) + ((b.ak ?? 1) - (a.ak ?? 1)) * t;
        this.outLoc.set(
          a.loc[0] + (b.loc[0] - a.loc[0]) * t,
          a.loc[1] + (b.loc[1] - a.loc[1]) * t,
          a.loc[2] + (b.loc[2] - a.loc[2]) * t,
        );
        this.qa.set(...a.lq);
        this.qb.set(...b.lq);
        this.tmpQ2.slerpQuaternions(this.qa, this.qb, t);
        this.groundSpeed =
          (Math.hypot(b.loc[0] - a.loc[0], b.loc[1] - a.loc[1], b.loc[2] - a.loc[2]) /
            Math.max(1, b.time - a.time)) *
          1000;
      }
    }

    // Secondary wing motion must use the same co-rotating planet frame as the
    // local player; orbital carry and floating-origin rebases are not inertia.
    if (this.avatar) this.avatar.clothFrame = aPi >= 0 && ak > .5 ? sys?.planets[aPi]?.group ?? null : null;

    // --- 3) Correction decay: any discontinuity in the sampled LOCAL pose
    // is absorbed and bled off (6/s) — never a visible step ---
    if (aPi >= 0 && sys) {
      if (this.corrPi === aPi && this.hasPrevLoc) {
        const step = this.outLoc.distanceTo(this.prevLoc);
        const allow = 0.35 + Math.max(this.groundSpeed ?? 0, 12) * dt * 3;
        if (step > allow && step < 60) {
          this.corr.add(this.prevLoc).sub(this.outLoc);
          if (this.corr.length() > 12) this.corr.set(0, 0, 0); // true teleport: snap
        }
      } else {
        this.corr.set(0, 0, 0);
      }
      this.corrPi = aPi;
      this.prevLoc.copy(this.outLoc);
      this.hasPrevLoc = true;
      this.corr.multiplyScalar(Math.exp(-6 * dt));
      this.outLoc.add(this.corr);
      if (!this.anchoredWorld(sys, aPi, this.outLoc, this.tmpQ2, origin, this.aPos, this.aQuat)) {
        aPi = -1;
      }
    } else {
      this.hasPrevLoc = false;
      this.corrPi = -1;
    }

    // --- 4) Blend the two spaces by the anchor fade: exact ground lock at
    // k=1, pure world interp at k=0, CONTINUOUS in between — crossing the
    // carry band never steps the rendered position ---
    if (aPi >= 0 && ak > 0) {
      this.sysPos.lerpVectors(this.wPos, this.aPos, ak);
      this.quat.copy(this.wQuat).slerp(this.aQuat, ak);
      // Streamed velocity FOLDS IN the planet carry (extrapolation needs
      // it) — but exhaust must read GROUND-relative motion, or a parked
      // ship's afterburners burn at the planet's rotation speed.
      if (this.groundSpeed !== null) {
        this.speed = Math.sign(this.speed || 1) * Math.min(Math.abs(this.speed), this.groundSpeed);
      }
    } else {
      this.sysPos.copy(this.wPos);
      this.quat.copy(this.wQuat);
      this.groundSpeed = null;
    }

    // --- Parked hull: anchored pair when available, else world interp ---
    this.parkedAnchored = false;
    if (!past && a.spi !== undefined && a.spi === b.spi && a.sloc && b.sloc && a.slq && b.slq && sys) {
      this.tmpV.set(
        a.sloc[0] + (b.sloc[0] - a.sloc[0]) * t,
        a.sloc[1] + (b.sloc[1] - a.sloc[1]) * t,
        a.sloc[2] + (b.sloc[2] - a.sloc[2]) * t,
      );
      this.qa.set(...a.slq);
      this.qb.set(...b.slq);
      this.tmpQ2.slerpQuaternions(this.qa, this.qb, t);
      if (this.anchoredWorld(sys, a.spi, this.tmpV, this.tmpQ2, origin, this.parkedPos, this.parkedQuat)) {
        // Ground-relative hull speed straight from the local pair — the
        // world-differenced fallback reads the planet's rotation as thrust.
        this.parkedSpeed =
          (Math.hypot(b.sloc[0] - a.sloc[0], b.sloc[1] - a.sloc[1], b.sloc[2] - a.sloc[2]) /
            Math.max(1, b.time - a.time)) *
          1000;
        this.parkedAnchored = true;
      }
    } else if (past && latest.spi !== undefined && latest.sloc && latest.slq && sys) {
      this.tmpV.set(...latest.sloc);
      this.tmpQ2.set(...latest.slq);
      if (this.anchoredWorld(sys, latest.spi, this.tmpV, this.tmpQ2, origin, this.parkedPos, this.parkedQuat)) {
        this.parkedSpeed = 0; // holding the local pose: hull is still
        this.parkedAnchored = true;
      }
    } else if (!past && a.sp && b.sp) {
      this.parkedPos.set(
        a.sp[0] + (b.sp[0] - a.sp[0]) * t,
        a.sp[1] + (b.sp[1] - a.sp[1]) * t,
        a.sp[2] + (b.sp[2] - a.sp[2]) * t,
      );
      if (a.sq && b.sq) {
        this.qa.set(...a.sq);
        this.qb.set(...b.sq);
        this.parkedQuat.slerpQuaternions(this.qa, this.qb, t);
      }
    }
  }

  /** Difference the autopilot pose for trail emission (teleport-guarded). */
  trackParkedSpeed(dt: number): void {
    if (this.hasLastParked && dt > 1e-4) {
      const v = this.lastParked.distanceTo(this.parkedPos) / dt;
      this.parkedSpeed = v > 20000 ? 0 : v;
    }
    this.lastParked.copy(this.parkedPos);
    this.hasLastParked = true;
  }

  dispose(scene: THREE.Scene): void {
    this.bubble.clear();
    scene.remove(this.group);
    disposeShip(this.built);
    this.vehicleView?.dispose();
    this.trails.dispose();
    this.avatar?.dispose(scene);
  }
}

export type NetStatus = "connecting" | "online" | "offline";

export class NetClient {
  readonly remotes = new Map<number, RemoteShip>();
  /** Relayed melee swings since the last drainPunches(). `origin` = the attacker's position, `dir` = their
   *  camera aim (the LEGACY CONE reads it), `a` = the validated CombatActionId they threw, `q` = the
   *  attacker's body rotation at receipt (the frame HIT_ARCS are baked in — the victim's prediction sweeps
   *  the arc in it). `dmg` is the attacker's CLAIM and is deliberately NOT used for damage: the server reads
   *  the def, so predicting the claim guarantees a contradiction. Kept only for the flag-off legacy path. */
  private punchHits: {
    origin: [number, number, number];
    dir: [number, number, number];
    dmg?: number;
    a?: CombatActionId;
    q: [number, number, number, number];
  }[] = [];
  /** Relayed melee strikes since the last drainDroneMelee() (origin = strike pos; id = attacker drone, for parry knockback). */
  private droneMeleeHits: { id: number; origin: [number, number, number]; dir: [number, number, number]; dmg: number; reach: number; wu?: number }[] = [];
  /** Authoritative bolt impacts since the last drainBoltHits() — the server's hit truth
   *  (Phase 1: drone hits; Phase 2 adds char hits). Drives damage numbers + confirmed FX. */
  private boltHits: { by: number; k: "drone" | "char"; id: number; dmg: number; point: [number, number, number] }[] = [];
  /** Monotonic fire sequence stamped onto each server-authoritative shot intent. */
  private fireSeq = 0;
  /** Frame tick for staggering mid-distance gait updates. */
  private lodFrame = 0;
  private dockM = new THREE.Matrix4();
  private dockV = new THREE.Vector3();
  private dockUp = new THREE.Vector3();
  private dockZero = new THREE.Vector3();
  private riderPos = new THREE.Vector3();
  private riderQuat = new THREE.Quaternion();
  status: NetStatus = "offline";
  playerId = -1;
  private ws: WebSocket | null = null;
  private sendTimer: number | null = null;
  private retryDelay = 1000;
  private timeOffset = 0; // serverTime - performance.now()
  private hasSync = false;
  private revealWaiters = new Map<string, {
    resolve: (reveal: import("@hyperlight/shared").SystemRevealV1) => void;
    reject: (error: Error) => void;
    timer: number;
  }>();

  /**
   * Server epoch SECONDS (EMA-synced), or null before the first states
   * message. The deterministic universe clock leashes to this so every
   * client (and the server's own claim validation) evaluates planet poses
   * on the same clock — local wall-clock skew stops mattering.
   */
  /** Market-board state for the LOCAL player. */
  onBoardState:
    | ((listings: import("@hyperlight/shared").BoardListing[], seq: number, err?: string) => void)
    | null = null;

  sendBoard(msg: {
    seq: number;
    op: "browse" | "list" | "bid" | "buyout" | "cancel" | "claim";
    id?: string;
    item?: string;
    n?: number;
    price?: number;
    minBid?: number;
    durationS?: number;
  }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "board", ...msg }));
    }
  }

  /** Escrow trade updates for the LOCAL player. */
  onTradeState:
    | ((s: {
        phase: "invite" | "open" | "done" | "cancelled";
        peer?: { id: number; name: string };
        you?: import("@hyperlight/shared").TradeOffer;
        them?: import("@hyperlight/shared").TradeOffer;
        youConfirmed?: boolean;
        themConfirmed?: boolean;
        err?: string;
      }) => void)
    | null = null;

  sendTrade(msg: {
    op: "invite" | "accept" | "decline" | "offer" | "confirm" | "cancel";
    target?: number;
    offer?: import("@hyperlight/shared").TradeOffer;
  }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "trade", ...msg }));
    }
  }

  /** Station quote sheet / trade ack for the LOCAL player. */
  onMarketState:
    | ((prices: Record<string, { sell: number; buy: number }>, seq: number, err?: string) => void)
    | null = null;

  sendMarket(msg: { seq: number; op: "quote" | "buy" | "sell"; id?: string; n?: number }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "market", ...msg }));
    }
  }

  /** Authoritative HEALTH push for the LOCAL player (server owns HP under
   *  SERVER_AUTHORITATIVE_HP). `dead` marks a kill — the game plays its death UX + respawns. */
  onHealth: ((hp: number, max: number, dead: boolean, sh?: number, shMax?: number) => void) | null = null;
  /** Relationship echo after a talk intent (NPC dialog panel). */
  onNpcRel: ((msg: import("@hyperlight/shared").NpcRelMsg) => void) | null = null;

  onNpcShop: ((msg: import("@hyperlight/shared").NpcShopStateMsg) => void) | null = null;
  sendNpcShop(msg: import("@hyperlight/shared").NpcShopMsg): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg)); return true;
  }

  /** Authoritative inventory push for the LOCAL player (owner-only). It also carries the
   *  server-owned combat UPGRADE levels + any purchase refusal (they share the account echo). */
  onInvState:
    | ((inventory: unknown, credits: number, fuelKit: boolean, seq: number, upgrades?: unknown, err?: string) => void)
    | null = null;

  sendInvIntent(msg: Omit<import("@hyperlight/shared").InvIntentMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "inv", ...msg }));
    }
  }

  /** Authoritative garage + wardrobe for the LOCAL player (owner-only, v49).
   *  The `gear` tokens already include the implicit starter grants. */
  onCollection: ((msg: import("@hyperlight/shared").CollectionStateMsg) => void) | null = null;

  /** Draw shield points into action energy (v50) — the server owns the shield side. */
  sendShieldDraw(n: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "shdraw", n }));
    }
  }

  /** Pick the active garage vehicle, or re-read the collection after a reconnect. */
  sendCollection(msg: Omit<import("@hyperlight/shared").CollectionMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "collection", ...msg }));
    }
  }

  /** Ask the server to buy the next level in a combat-energy track. */
  sendUpgrade(track: string, seq: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "upgrade", seq, track }));
    }
  }

  /** Owner-only quest-state push (active instances + completed ids). */
  onQuestState: ((msg: import("@hyperlight/shared").QuestStateMsg) => void) | null = null;
  /** Owner-only bounded authored-story consequence state. */
  onStoryState: ((msg: import("@hyperlight/shared").StoryStateMsg) => void) | null = null;
  /** Board contents reply for a (town, epoch) browse/accept. */
  onQuestBoard: ((msg: import("@hyperlight/shared").QuestBoardMsg) => void) | null = null;

  /** A quest action (accept/abandon/turnin/deliver/reach/board). The server
   *  re-derives the def from the id and validates proximity/epoch/inventory. */
  sendQuestIntent(m: Omit<import("@hyperlight/shared").QuestIntentMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "quest", ...m }));
    }
  }

  /** Owner-only homestead state (every beacon in the current system) + intent refusals. */
  onHsState: ((msg: import("@hyperlight/shared").HomesteadStateMsg) => void) | null = null;
  /** A live beacon change broadcast to everyone in the room (add/update/remove). */
  onHsDelta: ((msg: import("@hyperlight/shared").HomesteadDeltaMsg) => void) | null = null;
  /** Seq-acked owner-only offline-production state. */
  onSettlementState: ((msg: import("@hyperlight/shared").SettlementStateMsg) => void) | null = null;
  /** Public resident identities/assignments for current-system world rendering. */
  onSettlementPublic: ((msg: import("@hyperlight/shared").SettlementPublicMsg) => void) | null = null;

  /** Plant a claim beacon (server validates ownership/proximity/economy/caps). */
  sendClaim(m: Omit<import("@hyperlight/shared").ClaimMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "claim", ...m }));
  }
  /** Grow an owned beacon to a higher tier (pays the credit difference). */
  sendExpand(m: Omit<import("@hyperlight/shared").ExpandMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "expand", ...m }));
  }
  /** Add/remove a uid on an owned beacon's build/edit allowlist. */
  sendPermit(m: Omit<import("@hyperlight/shared").PermitMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "permit", ...m }));
  }
  /** Place a building piece (server validates permission/containment/budget/cost). */
  sendPlace(m: Omit<import("@hyperlight/shared").PlaceMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "place", ...m }));
  }
  /** Demolish a placed piece by pid (refunds a material fraction). */
  sendDemolish(m: Omit<import("@hyperlight/shared").DemolishMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "demolish", ...m }));
  }
  /** Abandon an owned claim (owner-only; server refuses while pieces remain). */
  sendAbandon(m: Omit<import("@hyperlight/shared").AbandonMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "abandon", ...m }));
  }
  /** Manage the physical settlement under the player. The server revalidates
   * claim presence, inventory, geometry, workers, and elapsed work. */
  sendSettlement(m: Omit<import("@hyperlight/shared").SettlementIntentMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "settlement", ...m }));
  }

  /** A relayed chat line (room/party/guild/whisper) or a private notice. */
  onChatMsg: ((msg: import("@hyperlight/shared").ChatMsg) => void) | null = null;

  /** Recent room scrollback delivered on join/warp — history, never bubbled. */
  onChatHist: ((msg: import("@hyperlight/shared").ChatHistMsg) => void) | null = null;

  /** Seq-acked full social state (login seed, op acks, presence pushes). */
  onSocialState: ((msg: import("@hyperlight/shared").SocialStateMsg) => void) | null = null;

  /** Send one friends-list op (seq already stamped by SocialModel.op). */
  sendSocial(m: import("@hyperlight/shared").SocialMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /** Full party state (ops, presence changes, the 1 Hz vitals tick). */
  onPartyState: ((msg: import("@hyperlight/shared").PartyStateMsg) => void) | null = null;

  /** Send one party op (seq already stamped by PartyModel.op). */
  sendParty(m: import("@hyperlight/shared").PartyMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /**
   * The cache instance the server has opened (or re-opened) for us, plus its live run
   * progress. Arrives in reply to {@link sendCacheEnter} AND unprompted whenever the
   * server's run state changes (a key picked up, a gate opened, the boss down), so the
   * handler must be idempotent: same instance → apply progress, new instance → enter it.
   */
  onCacheState: ((msg: import("@hyperlight/shared").CacheStateMsg) => void) | null = null;

  /**
   * CONTAINMENT — the cache envelope rule, addressed to US alone. Edge-triggered: a
   * warn-band crossing (`warn`), or a relocation (`to`, pad-local x/z). Never a stream.
   */
  onCacheEnv: ((msg: import("@hyperlight/shared").CacheEnvMsg) => void) | null = null;

  /**
   * "I am standing at this aperture — open my instance."
   *
   * `site` is a CLAIM and nothing more: the server re-derives the site from the system
   * it has us in, refuses a distant one, and picks the key (party), epoch, theme and
   * tier itself. There is no instance id to send — the reply carries it.
   */
  sendCacheEnter(site: string, tier: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "cacheEnter", site, tier }));
    }
  }

  /** "I'm done with this instance" — the server's cue to start the reconnect grace.
   *  Leaving itself is just joining the origin system again. */
  sendCacheLeave(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "cacheLeave" }));
    }
  }

  /** Full guild state (login seed + reconcile notice, op acks, roster changes). */
  onGuildState: ((msg: import("@hyperlight/shared").GuildStateMsg) => void) | null = null;

  /** Send one guild op (seq already stamped by GuildModel.op). */
  sendGuild(m: import("@hyperlight/shared").GuildMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /** Send one chat line. The server sanitizes the text, stamps the sender
   *  identity from the session (never from this message), and routes by `ch`. */
  sendChat(m: Omit<import("@hyperlight/shared").ChatSendMsg, "t">): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "chat", ...m }));
  }

  /** Server-verified god-mode privilege for THIS player (set from `welcome`). */
  god = false;
  /** True iff the SERVER verified this connection's login (welcome.authed) — a
   *  false here while the app is signed in means we were seated as a GUEST:
   *  the server's account echoes are a blank guest inventory and must not be
   *  adopted over locally-restored state (see main.ts onInvState). */
  authed = false;
  /** Fired when the welcome lands with the server-decided god flag. */
  onGod: ((on: boolean) => void) | null = null;

  /** God-mode: ask the server to grant `n` of item `id` into our inventory.
   *  No-op server-side unless this connection authenticated as god. */
  sendGodSpawn(id: string, n: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "godSpawn", id, n }));
    }
  }

  /** God-mode: ask the server to add `amount` credits to our balance (negative removes).
   *  No-op server-side unless this connection authenticated as god. */
  sendGodCredits(amount: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "godCredits", amount }));
    }
  }

  serverNow(): number | null {
    return this.hasSync ? (performance.now() + this.timeOffset) / 1000 : null;
  }
  onWildlife: ((msg:WildlifeMsg)=>void)|null = null;
  private closed = false;

  constructor(
    private scene: THREE.Scene,
    private name: string,
    private system: string,
    private getState: () => PlayerState,
    private getShip: () => ShipLayout,
    private getStyle: () => CharacterStyle,
    private onStatus: (s: NetStatus, players: number) => void,
    private onRemoteFire: (ship: RemoteShip, g: 1 | 2, d?: [number, number, number], hardpoint?: string) => void,
    /** A player in the room warped out, leaving a joinable wormhole. */
    private onRemoteWarp: (to: string, p: [number, number, number]) => void,
    /** Server-authoritative drone events. */
    private drones: DroneEvents,
    /** Fresh Firebase ID token, or null in guest mode. */
    private getAuthToken: () => Promise<string | null> = async () => null,
    /** Live star system — remote players anchor to its rotating bodies. */
    private getSystem: () => StarSystem | null = () => null,
    /** Admission ticket cached with a sealed-system reveal. */
    private getAccessTicket: (system: string) => string | null = () => null,
    /** Which of the account's characters this session plays (`c<slot>`). */
    private getCharacterId: () => string | null = () => null,
  ) {}

  connect(): void {
    this.closed = false;
    this.open();
  }

  private url(): string {
    // `?rt=http://host:port` points the WHOLE session (WS + geckos signaling) at another
    // realtime server — how we drive a test client against the UDP-capable GCE VM.
    const q = new URLSearchParams(location.search).get("rt");
    if (q) {
      const u = new URL(q);
      return `${u.protocol === "https:" ? "wss:" : "ws:"}//${u.host}/ws`;
    }
    const override = import.meta.env.VITE_WS_URL as string | undefined;
    if (override) return override;
    // Same-origin everywhere: vite proxies /ws in dev, nginx in production.
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/ws`;
  }

  private open(): void {
    this.setStatus("connecting");
    const ws = new WebSocket(this.url());
    this.ws = ws;

    ws.onopen = () => {
      this.retryDelay = 1000;
      void this.getAuthToken().then((auth) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const access = this.getAccessTicket(this.system);
        const character = this.getCharacterId();
        ws.send(
          JSON.stringify({
            t: "join",
            v: PROTOCOL_VERSION,
            name: this.name,
            system: this.system,
            state: this.getState(),
            ship: this.getShip(),
            style: this.getStyle(),
            ...(auth ? { auth } : {}),
            ...(character ? { character } : {}),
            ...(access ? { access } : {}),
          }),
        );
      });
    };

    ws.onmessage = (ev) => this.handleServerMsg(String(ev.data));

    ws.onclose = () => {
      this.teardownConnection();
      if (this.closed) return;
      this.setStatus("offline");
      setTimeout(() => this.open(), this.retryDelay);
      this.retryDelay = Math.min(15000, this.retryDelay * 2);
    };
    ws.onerror = () => ws.close();
  }

  /** One handler for BOTH transports: the reliable WebSocket and the unreliable (geckos UDP)
   *  channel feed the same message switch, so a snapshot is a snapshot wherever it arrived. */
  private handleServerMsg(raw: string): void {
      const msg = parseServerMsg(raw);
      if (!msg) return;
      switch (msg.t) {
        case "welcome":
          this.playerId = msg.id;
          this.setStatus("online");
          this.god = msg.god === true;
          this.authed = msg.authed === true; // set BEFORE onGod so the callback can read both
          this.onGod?.(this.god);
          for (const p of msg.players) this.addRemote(p);
          if (this.sendTimer === null) {
            this.sendTimer = window.setInterval(() => this.sendState(), 1000 / CLIENT_SEND_HZ);
          }
          // Unreliable snapshot channel: the welcome's one-time token authorizes it.
          if (msg.rt && TRANSPORT_UNRELIABLE) this.connectRt(msg.rt);
          break;
        case "joined":
          this.addRemote(msg.player);
          break;
        case "left":
          this.removeRemote(msg.id);
          break;
        case "refit":
          if (msg.id !== this.playerId) this.remotes.get(msg.id)?.refit(msg.ship);
          break;
        case "style":
          if (msg.id !== this.playerId) this.remotes.get(msg.id)?.restyle(msg.style);
          break;
        case "fire": {
          const ship = this.remotes.get(msg.id);
          if (ship) this.onRemoteFire(ship, msg.g, msg.d, msg.vh);
          break;
        }
        case "punch": {
          const ship = this.remotes.get(msg.id);
          if (ship) {
            // OBSERVER VISUAL: play the action the attacker actually threw. `a` (v23) exists for
            // exactly this — without reading it, every sword swing in the room rendered as a
            // generic `punch_f` jab. Unknown/absent id (an old client) → the generic throw.
            const def = msg.a ? combatAction(msg.a) : null;
            if (def && ship.avatar?.playRelayedAction(def.id)) {
              /* played the action's own clip */
            } else ship.avatar?.move("punch_f");
            // Queue the swing so the game layer can predict damage on the LOCAL char. `q` is the
            // ATTACKER'S BODY ROTATION at receipt: HIT_ARCS are baked in the character-local frame,
            // so the victim cannot place the arc without it. COPIED, not aliased — `ship.quat` is a
            // live scratch object the sampler rewrites every frame.
            this.punchHits.push({
              origin: ship.netPos,
              dir: msg.d,
              dmg: msg.dmg,
              a: def?.id,
              q: [ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w],
            });
          }
          break;
        }
        case "warp":
          if (msg.id !== this.playerId) this.onRemoteWarp(msg.to, msg.p);
          break;
        case "drones":
          if (msg.time < this.lastDronesTime) break; // stale (unordered UDP reorder) — drop
          this.lastDronesTime = msg.time;
          this.drones.snapshot(msg.list, msg.time);
          break;
        case 'wildlife':
          this.onWildlife?.(msg);
          break;
        case "dfire":
          this.drones.fire(msg);
          break;
        case "dmelee":
          this.drones.melee(msg); // attacker lunge/combo + impact (all clients)
          // Queue for the local cone/range test (relay model, like punches).
          this.droneMeleeHits.push({ id: msg.id, origin: msg.p, dir: msg.d, dmg: msg.dmg, reach: msg.reach, wu: msg.wu });
          break;
        case "dact":
          this.drones.act(msg); // one-shot humanoid action (dodge-roll)
          break;
        case "ddeath":
          this.drones.death(msg);
          break;
        case "bhit":
          // Authoritative projectile impact — the server's hit truth (drone hp still
          // arrives via `drones`; this drives damage numbers + confirmed impact FX).
          this.boltHits.push({ by: msg.by, k: msg.k, id: msg.id, dmg: msg.dmg, point: msg.p });
          break;
        case "loot":
          this.drones.loot(msg.items);
          break;
        case "nodes":
          this.drones.nodes(msg.list);
          break;
        case "mined":
          this.drones.mined(msg);
          break;
        case "hp":
          this.onHealth?.(msg.hp, msg.max, msg.dead === true, msg.sh, msg.shMax);
          break;
        case "invState":
          this.onInvState?.(msg.inventory, msg.credits, msg.fuelKit, msg.seq, msg.upgrades, msg.err);
          break;
        case "collectionState":
          this.onCollection?.(msg);
          break;
        case "npcShopState":
          this.onNpcShop?.(msg);
          break;
        case "npcRel":
          this.onNpcRel?.(msg);
          break;
        case "questState":
          this.onQuestState?.(msg);
          break;
        case "storyState":
          this.onStoryState?.(msg);
          break;
        case "questBoard":
          this.onQuestBoard?.(msg);
          break;
        case "hsState":
          this.onHsState?.(msg);
          break;
        case "settlementState":
          this.onSettlementState?.(msg);
          break;
        case "settlementPublic":
          this.onSettlementPublic?.(msg);
          break;
        case "chatMsg":
          this.onChatMsg?.(msg);
          break;
        case "chatHist":
          this.onChatHist?.(msg);
          break;
        case "socialState":
          this.onSocialState?.(msg);
          break;
        case "guildState":
          this.onGuildState?.(msg);
          break;
        case "cacheState":
          this.onCacheState?.(msg);
          break;
        case "cacheEnv":
          this.onCacheEnv?.(msg);
          break;
        case "ptag":
          this.remotes.get(msg.id)?.retag(msg.tag);
          break;
        case "partyState":
          this.onPartyState?.(msg);
          break;
        case "hsDelta":
          this.onHsDelta?.(msg);
          break;
        case "marketState":
          this.onMarketState?.(msg.prices, msg.seq, msg.err);
          break;
        case "tradeState":
          this.onTradeState?.(msg);
          break;
        case "boardState":
          this.onBoardState?.(msg.listings, msg.seq, msg.err);
          break;
        case "states": {
          if (msg.time < this.lastStatesTime) break; // stale (unordered UDP reorder) — drop
          this.lastStatesTime = msg.time;
          // EMA time sync keeps interpolation stable under jitter.
          const offset = msg.time - performance.now();
          this.timeOffset = this.hasSync ? this.timeOffset * 0.9 + offset * 0.1 : offset;
          this.hasSync = true;
          for (const s of msg.states) {
            if (s.id === this.playerId) continue;
            this.remotes.get(s.id)?.push(msg.time, s.s, this.getSystem());
          }
          break;
        }
        case "error":
          if (msg.message === "session-superseded") {
            // Another device signed in: stand down for good (no reconnect
            // tug-of-war between two screens).
            this.closed = true;
            this.onKicked?.();
          }
          console.warn("[net] server error:", msg.message);
          break;
        case "systemReveal":
          this.handleSystemReveal(msg);
          break;
      }
  }

  /** Main installs the decrypt/register hook. The request promise resolves
   *  only after that asynchronous work completes. */
  onSystemReveal: ((reveal: import("@hyperlight/shared").SystemRevealV1) => Promise<void> | void) | null = null;

  private handleSystemReveal(msg: SystemRevealMsg): void {
    const waiter = this.revealWaiters.get(msg.systemId);
    if ("error" in msg) {
      if (waiter) {
        window.clearTimeout(waiter.timer);
        this.revealWaiters.delete(msg.systemId);
        waiter.reject(new Error(msg.error));
      }
      return;
    }
    void Promise.resolve(this.onSystemReveal?.(msg.reveal)).then(() => {
      if (!waiter) return;
      window.clearTimeout(waiter.timer);
      this.revealWaiters.delete(msg.systemId);
      waiter.resolve(msg.reveal);
    }).catch((error: unknown) => {
      if (!waiter) return;
      window.clearTimeout(waiter.timer);
      this.revealWaiters.delete(msg.systemId);
      waiter.reject(error instanceof Error ? error : new Error(String(error)));
    });
  }

  requestSystemReveal(system: string): Promise<import("@hyperlight/shared").SystemRevealV1> {
    const existing = this.revealWaiters.get(system);
    if (existing) return Promise.reject(new Error("system reveal already in progress"));
    if (this.ws?.readyState !== WebSocket.OPEN) return Promise.reject(new Error("not connected"));
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.revealWaiters.delete(system);
        reject(new Error("system reveal timed out"));
      }, 10_000);
      this.revealWaiters.set(system, { resolve, reject, timer });
      this.ws!.send(JSON.stringify({ t: "discover", system }));
    });
  }

  // --- Unreliable snapshot channel (geckos.io UDP; TRANSPORT_UNRELIABLE) -----------

  private rt: ClientChannel | null = null;
  private rtUp = false;
  private rtMsgs = 0;
  /** Newest accepted snapshot times — the UDP channel is UNORDERED, so a late datagram can
   *  carry an older snapshot than one already applied; those are dropped here. (No-op on the
   *  ordered WebSocket path.) */
  private lastStatesTime = 0;
  private lastDronesTime = 0;

  /** Debug/probe: channel state + how many messages have arrived over UDP. */
  rtStatus(): { connected: boolean; msgs: number } {
    return { connected: this.rtUp, msgs: this.rtMsgs };
  }

  /** Where geckos signaling lives: the `?rt=` override (VM testing), else the VITE_WS_URL
   *  override's origin, else this page's origin (vite proxies /.wrtc in dev; same origin in prod). */
  private rtBase(): { url: string; port: number } {
    const q = new URLSearchParams(location.search).get("rt");
    const wsOverride = import.meta.env.VITE_WS_URL as string | undefined;
    const base = q ?? (wsOverride ? wsOverride.replace(/^ws/, "http").replace(/\/ws$/, "") : location.origin);
    const u = new URL(base);
    return { url: `${u.protocol}//${u.hostname}`, port: u.port ? Number(u.port) : u.protocol === "https:" ? 443 : 80 };
  }

  private connectRt(token: string): void {
    this.rt?.close();
    this.rtUp = false;
    const base = this.rtBase();
    const channel = geckos({ url: base.url, port: base.port, authorization: token });
    this.rt = channel;
    channel.onConnect((err) => {
      // Connect failure (UDP blocked, old browser) is fine: the server never binds the
      // channel, so snapshots simply keep arriving over the WebSocket.
      if (!err) this.rtUp = true;
      else console.warn("[rt] unreliable channel unavailable, staying on WebSocket:", err.message);
    });
    channel.on("m", (data) => {
      this.rtMsgs++;
      this.handleServerMsg(String(data));
    });
    channel.onDisconnect(() => {
      this.rtUp = false; // server unbinds too → snapshots fall back to WS
    });
  }

  /** Also called on stance/equipment edges, before subsequent reliable fire intents. */
  sendState(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "state", state: this.getState() }));
    }
  }

  /** Announce a new ship layout (after a shipyard save). */
  sendRefit(ship: ShipLayout): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "refit", ship }));
    }
  }

  /** Announce a new character style (after a suit-locker change). */
  sendStyle(style: CharacterStyle): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "style", style }));
    }
  }

  /** Talk to a settlement NPC (server validates against the analytic pose). */
  sendTalk(npc: string, op?: "end" | "hold"): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "talk", npc, op }));
    }
  }

  /**
   * Announce a weapon-group shot. Always relays the tracer to other pilots; when `intent`
   * is supplied (server-authoritative bolts on), it also carries the fire INTENT the server
   * simulates from — muzzle origin, planet anchor, the view-time it aimed at (`ct`), and the
   * weapon id (the server reads speed/damage from the def). A monotonic `seq` is stamped on.
   */
  sendFire(
    g: 1 | 2,
    d?: [number, number, number],
    intent?: { o?: [number, number, number]; pl?: number; ct?: number; wp?: string; vh?: string; vw?: string },
  ): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const extra = intent ? { ...intent, seq: ++this.fireSeq } : {};
      this.ws.send(JSON.stringify({ t: "fire", g, ...(d ? { d } : {}), ...extra }));
    }
  }

  /**
   * The server-clock instant (ms) the local view currently represents: remotes are rendered
   * this far in the past (INTERP_DELAY_MS behind synced server time). Sent as a shot's `ct`
   * so the server rewinds targets to exactly what the shooter saw. Null before clock sync.
   */
  aimTimeMs(): number | null {
    return this.hasSync ? performance.now() + this.timeOffset - INTERP_DELAY_MS : null;
  }

  /** Announce a jab/melee strike (direction = aim) so others see it + can be hit by it. Auto-stamps the
   *  view time (`ct`) for the server's lag-comp melee test.
   *
   *  `action` is a CombatActionId — TYPED, not a bare string: the server rejects an id that is not in
   *  COMBAT_ACTIONS, so a typo'd literal here is a swing that silently deals no damage at all. Under
   *  SWEPT_MELEE the server re-runs THAT action's baked blade arc and
   *  reads the damage FROM THE DEF, so `dmg` is legacy there and ignored server-side — it is still sent for
   *  the flag-off cone path (and for a server that predates the action table). Omit `action` and the server
   *  falls back to that cone; send an unknown one and it rejects the strike (combatAction() → null). */
  sendPunch(d: [number, number, number], dmg?: number, action?: CombatActionId): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const ct = this.aimTimeMs();
      this.ws.send(JSON.stringify({
        t: "punch", d,
        ...(dmg !== undefined ? { dmg } : {}),
        ...(action !== undefined ? { a: action } : {}),
        ...(ct !== null ? { ct } : {}),
      }));
    }
  }

  /** Drain relayed melee swings received since the last call. The game PREDICTS damage on the local char
   *  from each (server-authoritative HP still reconciles): the action's baked arc when `a` names one, else
   *  the fists' cone. See the field declaration for what each member is. */
  drainPunches(): {
    origin: [number, number, number];
    dir: [number, number, number];
    dmg?: number;
    a?: CombatActionId;
    q: [number, number, number, number];
  }[] {
    const out = this.punchHits;
    this.punchHits = [];
    return out;
  }

  /** Drain relayed melee strikes since the last call. The game range-tests the
   *  local char against each for self-applied (blockable) damage. */
  drainDroneMelee(): { id: number; origin: [number, number, number]; dir: [number, number, number]; dmg: number; reach: number; wu?: number }[] {
    const out = this.droneMeleeHits;
    this.droneMeleeHits = [];
    return out;
  }

  /** Drain authoritative bolt impacts since the last call — the server's hit truth. Drives
   *  damage numbers + confirmed-impact FX (drone hp still arrives via the `drones` snapshot). */
  drainBoltHits(): { by: number; k: "drone" | "char"; id: number; dmg: number; point: [number, number, number] }[] {
    const out = this.boltHits;
    this.boltHits = [];
    return out;
  }

  /** Claim a bolt hit on a drone (the server validates and owns hp). */
  sendDroneHit(id: number, dmg: number, up?: number, a?: string, w?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        t: "dhit", id, dmg,
        ...(up && up > 0 ? { up } : {}),
        // The striking action id — the server caps `dmg` from ITS table damage (the
        // riposte's 3.5× clears validation without widening the flat blade ceiling).
        ...(a ? { a } : {}),
        // BOLT claim: the firing weapon id — the server derives damage/range caps from it.
        ...(w ? { w } : {}),
      }));
    }
  }

  /** Tell the server we finished the death UX and want full HP (server owns respawn). */
  sendRespawn(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "respawn" }));
    }
  }

  /** Report self-inflicted damage (fall/hazard) for the server to apply to our own HP. */
  sendSelfDamage(n: number, src: "fall" | "hazard"): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "selfdmg", n, src }));
    }
  }

  /** Claim a PARRY knockback on drone `id` — the server shoves it back along `dir` (world). */
  sendDroneKnock(id: number, dir: [number, number, number], force: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "dknock", id, dir, force }));
    }
  }

  /** Set by main: invoked when this session is superseded elsewhere. */
  onKicked: (() => void) | null = null;

  /** Claim one mining cycle on a node (the server validates + depletes). */
  sendMine(nodeId: number): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "mine", id: nodeId }));
    }
  }

  /** God-gated enemy spawn: kind + group size + world placement + scatter.
   *  `calm` = spawn on a tight guard leash (idle life until provoked). */
  sendDroneSpawn(kind?: string, n?: number, at?: [number, number, number], spread?: number, calm?: boolean): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          t: "dspawn",
          ...(kind ? { kind } : {}),
          ...(n != null ? { n } : {}),
          ...(at ? { at } : {}),
          ...(spread != null ? { spread } : {}),
          ...(calm ? { calm: true } : {}),
        }),
      );
    }
  }

  /** Announce a warp-out so the room sees the joinable wormhole. */
  sendWarp(to: string, p: [number, number, number]): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: "warp", to, p }));
    }
  }

  private addRemote(p: RemotePlayer): void {
    if (p.id === this.playerId || this.remotes.has(p.id)) return;
    const ship = new RemoteShip(p.id, p.name, p.ship ?? DEFAULT_LAYOUT, p.style, this.scene, p.tag);
    ship.push(performance.now() + this.timeOffset, p.state, this.getSystem());
    this.remotes.set(p.id, ship);
    this.scene.add(ship.group);
    this.setStatus(this.status);
  }

  private removeRemote(id: number): void {
    const ship = this.remotes.get(id);
    if (!ship) return;
    ship.dispose(this.scene);
    this.remotes.delete(id);
    this.setStatus(this.status);
  }

  /** Overhead bubble for a relayed LOCAL line — character names are globally
   *  unique and `from` is server-stamped, so a name lookup is authoritative.
   *  False = speaker not rendered here (raced a leave); nothing to show. */
  showBubbleFor(name: string, text: string): boolean {
    for (const r of this.remotes.values()) {
      if (r.name === name) {
        r.showBubble(text);
        return true;
      }
    }
    return false;
  }

  /** Live remote bubbles (probes): speaker name → bubble text. */
  bubbles(): { name: string; text: string }[] {
    const out: { name: string; text: string }[] = [];
    for (const r of this.remotes.values()) {
      if (r.bubble.text !== null) out.push({ name: r.name, text: r.bubble.text });
    }
    return out;
  }

  /** Hide every remote (ships, avatars, trails) while an editor owns the
   *  screen — the void must not contain other people's traffic. */
  private worldVisible = true;

  setWorldVisible(visible: boolean): void {
    this.worldVisible = visible;
    for (const r of this.remotes.values()) {
      r.group.visible = visible;
      if (r.avatar) r.avatar.group.visible = visible && (r.kind === "foot" || r.kind === "vehicle");
      r.trails.setVisible(visible && r.kind === "ship");
    }
  }

  private stDef: StationDef | null = null;
  private stDefFor: string | null = null;

  /** Per-frame: interpolate remote players and convert to render space.
   *  `worldTime` is the viewer's render clock — docked ships are DERIVED
   *  from the shared station math on it, identical on every screen. */
  update(dt: number, origin: THREE.Vector3, camPos: THREE.Vector3, worldTime = 0): void {
    if (!this.hasSync) return;
    this.lodFrame++;
    const renderTime = performance.now() + this.timeOffset - INTERP_DELAY_MS;
    const sys = this.getSystem();
    if (sys && this.stDefFor !== sys.def.id) {
      this.stDef = stationFor(sys.def);
      this.stDefFor = sys.def.id;
    }
    for (const ship of this.remotes.values()) {
      ship.sample(renderTime, sys, origin, dt);
      if (ship.kind !== "foot" && ship.dockPad !== null && this.stDef) {
        // Docked: exact pad pose from the shared clock — zero jitter, zero
        // interpolation, every client in agreement.
        const w = padWorld(this.stDef, stationPose(this.stDef, worldTime), ship.dockPad);
        ship.sysPos.set(w.pos[0] + w.up[0] * 1.6, w.pos[1] + w.up[1] * 1.6, w.pos[2] + w.up[2] * 1.6);
        this.dockM.lookAt(
          this.dockZero.set(0, 0, 0),
          this.dockV.set(-w.nose[0], -w.nose[1], -w.nose[2]),
          this.dockUp.set(w.up[0], w.up[1], w.up[2]),
        );
        ship.quat.setFromRotationMatrix(this.dockM);
        ship.speed = 0;
      }
      const onFoot = ship.kind === "foot";
      const ridingVehicle = ship.kind === "vehicle";
      if (onFoot && ship.avatar) {
        // Character walks the interpolated state; the hull flies its orbit.
        ship.avatar.setMounted(false);
        ship.avatar.group.visible = this.worldVisible;
        ship.avatar.group.position.copy(ship.sysPos).sub(origin);
        ship.avatar.group.quaternion.copy(ship.quat);
        // Remotes have no input — infer run-vs-walk from their speed. The shared
        // driver maps the synced state → pose (same path humanoid enemies use).
        driveAvatar(
          ship.avatar,
          {
            weaponId: ship.weaponId,
            stowedId: ship.stowedId,
            jet: ship.netJet,
            flightPower:ship.netFlightPower,
            flightLaunchRemaining:ship.netFlightLaunchRemaining,
            divePreparation:ship.netDivePreparation,
            flightApproach:ship.netFlightApproach,
            wallRunState:ship.netWallRunState,
            wallRunTime:ship.netWallRunTime,
            ledgeClimbState:ship.netLedgeClimbState,ledgeClimbTime:ship.netLedgeClimbTime,ledgeAnchor:ship.netLedgeAnchor,
            aiming: ship.netAiming,
            airborne: ship.netAirborne,
            crouch: ship.netCrouch,
            blocking: ship.netBlocking,
            moveFwd: ship.netMoveFwd,
            moveRight: ship.netMoveRight,
            climb: ship.netClimb,
            pitch: ship.netPitch,
            groundSpeed: ship.groundSpeed ?? ship.vel.length(),
            submerge: ship.netSubmerge,
          },
          dt,
          camPos,
          this.lodFrame,
        );
        ship.group.position.copy(ship.parkedPos).sub(origin);
        ship.group.quaternion.copy(ship.parkedQuat);
        if (!ship.parkedAnchored) ship.trackParkedSpeed(dt);
      } else if (ridingVehicle && ship.avatar) {
        const def = vehicleDef(ship.vehicleId ?? "");
        ship.avatar.group.visible = this.worldVisible;
        if (!def || !vehicleRiderPose(def, ship.sysPos, ship.quat, this.riderPos, this.riderQuat)) {
          this.riderPos.copy(ship.sysPos);
          this.riderQuat.copy(ship.quat);
        }
        ship.avatar.group.position.copy(this.riderPos).sub(origin);
        ship.avatar.group.quaternion.copy(this.riderQuat);
        ship.avatar.setMounted(true, def ? driverSeat(def)?.riderPose : undefined);
        ship.avatar.update(dt, 0, true, false);
        ship.group.position.copy(ship.sysPos).sub(origin);
        ship.group.quaternion.copy(ship.quat);
      } else {
        ship.avatar?.setMounted(false);
        if (ship.avatar) ship.avatar.group.visible = false;
        ship.group.position.copy(ship.sysPos).sub(origin);
        ship.group.quaternion.copy(ship.quat);
      }
      ship.trails.setVisible(this.worldVisible && ship.kind === "ship");
      ship.trails.update(
        onFoot ? ship.parkedPos : ship.sysPos,
        onFoot ? ship.parkedQuat : ship.quat,
        onFoot ? ship.parkedSpeed : ship.speed,
        origin,
        camPos,
        dt,
      );
    }
  }

  private setStatus(s: NetStatus): void {
    this.status = s;
    this.onStatus(s, this.remotes.size + (s === "online" ? 1 : 0));
  }

  dispose(): void {
    this.closed = true;
    if (this.sendTimer !== null) clearInterval(this.sendTimer);
    this.sendTimer = null;
    this.ws?.close();
    for (const id of [...this.remotes.keys()]) this.removeRemote(id);
  }

  private teardownConnection(): void {
    if (this.sendTimer !== null) {
      clearInterval(this.sendTimer);
      this.sendTimer = null;
    }
    // The UDP channel's token died with the session; a reconnect's welcome mints a fresh one.
    this.rt?.close();
    this.rt = null;
    this.rtUp = false;
    this.lastStatesTime = 0;
    this.lastDronesTime = 0;
    for (const id of [...this.remotes.keys()]) this.removeRemote(id);
    this.playerId = -1;
    this.hasSync = false;
  }
}
